import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, AlertTriangle, Wrench, Sparkles, Megaphone, Check } from 'lucide-react';
import { apiClient } from '../../api/client';

export interface SystemNotice {
  id: string;
  title: string;
  body: string | null;
  type: string;
  color: string;
  revision: number;
}

const ICONS: Record<string, typeof Info> = {
  info: Info, warning: AlertTriangle, maintenance: Wrench, news: Sparkles, important: Megaphone,
};

/** Černý nebo bílý text podle jasu pozadí, aby byl banner čitelný v jakékoli barvě. */
export function textOn(hex: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) > 160 ? '#111827' : '#ffffff';
}

export function NoticeBar({ n, onAck, busy }: { n: SystemNotice; onAck?: () => void; busy?: boolean }) {
  const Icon = ICONS[n.type] ?? Info;
  const fg = textOn(n.color);
  const btnBg = fg === '#ffffff' ? 'rgba(255,255,255,0.2)' : 'rgba(17,24,39,0.12)';
  return (
    <div style={{ background: n.color, color: fg }}
         className="w-full px-4 py-2.5 flex items-start gap-3 text-sm shadow-sm">
      <Icon className="h-[18px] w-[18px] shrink-0 mt-px" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{n.title}</div>
        {n.body && <div className="whitespace-pre-line opacity-95 mt-0.5">{n.body}</div>}
      </div>
      {onAck && (
        <button onClick={onAck} disabled={busy} style={{ background: btnBg, color: fg }}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold shrink-0 self-center whitespace-nowrap hover:opacity-80 disabled:opacity-50">
          <Check className="h-3.5 w-3.5" /> Rozumím
        </button>
      )}
    </div>
  );
}

// Informativní hlášení od správce systému. Zavře se až potvrzením „Rozumím",
// a to za každého uživatele zvlášť (potvrzení se ukládá na serveru).
export default function NoticeBanner() {
  const qc = useQueryClient();
  const { data } = useQuery<SystemNotice[]>({
    queryKey: ['system-notices'],
    queryFn: () => apiClient.get('/notices/active').then(r => r.data.data),
    refetchInterval: 120000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const ack = useMutation({
    mutationFn: (id: string) => apiClient.post(`/notices/${id}/ack`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['system-notices'] });
      qc.setQueryData<SystemNotice[]>(['system-notices'], old => (old ?? []).filter(x => x.id !== id));
    },
    onError: () => qc.invalidateQueries({ queryKey: ['system-notices'] }),
  });
  if (!data?.length) return null;
  return (
    <div className="w-full shrink-0">
      {data.map(n => <NoticeBar key={n.id + ':' + n.revision} n={n} onAck={() => ack.mutate(n.id)} busy={ack.isPending} />)}
    </div>
  );
}
